import { FetchPolicy } from '@apollo/client';
import { MutationFetchPolicy } from '@apollo/client/core/watchQueryOptions';
import { get, sortBy } from 'lodash';
import { Moment } from 'moment';
import { LegacyOrderResponse } from '../../helpers';
import {
  FeaturesConnection,
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
} from './home';
import { Filter, FilterItem, IProduct, QuoteResponse } from './product';
export type { ScoreEntity } from './graphProduct';
export * from './home';
export * from './product';
export * from './triple-play';
export enum SortingType {
  Lowest_Price = 0,
  Highest_Price = 1,
}
export interface Comparison {
  criteria: { [fieldName: string]: string };
}

export type IComparatorApiServiceParameters = {
  baseUrl: string;
  baseUrlTSN?: string;
  tokenUrl?: string;
  tokenTSN?: string;
  credentials:
    | {
        user: string;
        password: string;
        token?: string;
        expiresIn?: Moment;
        clientId?: string;
        clientSecret?: string;
      }
    | {
        user?: string;
        password?: string;
        token: string;
        expiresIn?: Moment;
        clientId?: string;
        clientSecret?: string;
      }
    | {
        user?: string;
        password?: string;
        token: string;
        expiresIn?: Moment;
        clientId: string;
        clientSecret: string;
      };
};

export type CalculateParameters = {
  postalCode: string;
  houseNumber: string;
  effectiveDate: string;
  paymentTermInMonths?: number;
  homeSumInsured?: number;
  houseLetter?: string;
  constructionYear?: number;
  constructionOfRoof?: string;
  homeType?: string;
  propertyUseType?: string;
  rebuildCost?: number;
  consideredMonumentByOwner?: boolean;
  houseSurfaceAreaSquareMeters?: number;
  numberRooms?: number;
  constructionOfWalls?: string | number;
  constructionOfFloors?: string | number;
  propertyOfInsured?: boolean;
  volumeInCubicMeters?: number;
  wozAppraisalValue?: number;
  netMonthlyIncome?: number;
  insuredFamilyComposition?: string;
  includeCoverageBuildings?: boolean;
  includeCoverageHomes?: boolean;
  includeCoverageLiabilities?: boolean;
  birthDate?: string;
  burglaryPrevention?: string;
  recentBathroomKitchen?: number;
  solarPanels?: number;
  glass?: string;
  coverageCodeBuildings?: string;
  coverageCodeHome?: string;
  outsideCoverage?: number;
  deductibleAmount?: number | null;
  liabilitySumInsured?: number;
  audioTV?: number;
  computers?: number;
  collection?: number;
  jewelry?: number;
  antique?: number;
  art?: number;
  tenantInterest?: number;
  instruments?: number;
};

export type CalculateParametersTripleplay = {
  postalCode: string;
  houseNumber: string;
  houseNumberAddition: string;
  comparisonId: string;
  filters: string;
  retention?: string;
  productTypes?: string;
};

export type AddressData = {
  houseNumber: number;
  houseNumberAddition: string;
  houseLetter: string;
  street: string;
  city: string;
  postalCode: string;
  constructionYear: number;
  homeType: string;
  propertyUseType: string;
  rebuildCost: number;
  consideredMonumentByOwner: boolean;
  houseSurfaceAreaSquareMeters: number;
  numberRooms: number;
  propertyOfInsured: boolean;
  roofConstructionType: string;
  constructionOfRoof: string;
  wozAppraisalValue: number;
  volumeInCubicMeters: number;
  contentValueIndication: number;
  solarPanels: boolean;
  numberOfSolarPanels: number;
  constructionOfWalls: 'VH' | 'VT';
};
export interface IComparatorApiService extends IComparatorApiServiceParameters {
  getComparison: (id: string) => Promise<Comparison>;
  getCustomerBenefits: () => Promise<CustormerBenefitItem[]>;
  validateAddress: (
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ) => Promise<AddressApiSuccessResponse>;
  getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData>;
  getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection>;
  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure>;
  getFilters(products: IProduct[], filters: Filter[]): Filter[];
  sortProducts(
    products: IProduct[],
    reference: string,
    descending?: boolean
  ): IProduct[];
  getProducts(
    searchCriteria: object,
    selectedFilters?: object,
    requestOptions?: Parameters<typeof fetch>[1],
    quotePrefillProps?: { swiftUserId: string; swiftProductId: string },
    useOldValidation?: boolean
  ): Promise<IProduct[]>;
  orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string,
    affiliateIncentive?: {
      userDesiresIncentive: boolean;
      callName?: string;
      prefixes?: string;
      surname?: string;
      birthDate?: string;
      email?: string;
    },
    comparatorName?: string
  ): Promise<OrderResult>;
  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity?: boolean,
    intake?: boolean,
    continueCoverage?: boolean
  ): Promise<string>;
  sendCustomerData?: (
    firstName: string,
    email: string,
    productType: string
  ) => Promise<string>;
  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure>;
  orderStatus(orderId: string): Promise<OrderStatusResult>;
  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']>;
  getClient: () => any;
  getHeaders?: (headers: any) => any;
  setClient?: (client: any) => void;
  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> | undefined;
  checkMembershipStatus(email: string): Promise<any>;
  addEnergyAuction(variables: any): Promise<any>;
}

export type AddressApiSuccessResponse = {
  city: string;
  street: string;
  houseNumber: string;
  postcode: string;
  fullAddress: string;
  addition?: string;
};

export class AddressApiError extends Error {
  constructor(message) {
    super(message);
    this.name = 'unknown combination';
  }
}

export const houseNumberError = new AddressApiError(
  'Deze combinatie van postcode/\u200bhuisnummer kennen we helaas niet.'
);

export const defaultCalculateParameters = {
  houseLetter: '',
  paymentTermInMonths: 1,
  constructionYear: 1980,
  constructionOfRoof: 'VP',
  homeSumInsured: 100000,
  homeType: 'VU',
  propertyUseType: 'VP',
  rebuildCost: 100000.0,
  consideredMonumentByOwner: false,
  houseSurfaceAreaSquareMeters: 65,
  numberRooms: 4,
  propertyOfInsured: false,
  volumeInCubicMeters: 500,
  wozAppraisalValue: 100000.0,
  netMonthlyIncome: undefined,
  insuredFamilyComposition: 'VH',
  birthDate: '29-10-1966',
  burglaryPrevention: 'VB',
  recentBathroomKitchen: 1,
  solarPanels: 2,
  glass: 'V15',
  coverageCodeBuildings: 'V5007',
  coverageCodeHome: 'V5018',
  outsideCoverage: 0,
  deductibleAmount: 0,
  liabilitySumInsured: 1250000.0,
  audioTV: 0,
  computers: 0,
  collection: 0,
  jewlery: 0,
  antique: 0,
  art: 0,
  tenantInterest: 0,
};

export type CustormerBenefitItem = {
  label: string;
  value: string;
};

export const filterSustainabilityScore = (
  filter: Filter,
  fields: FilterItem[],
  newFilters
) => {
  if (filter.id === 'sustainabilityScore') {
    const filteredValues = fields.reduce((arr, { value }): any => {
      if (Number(value) >= (filter.props?.minValue || 0)) {
        return [...arr, value];
      }

      return [...arr];
    }, []);

    newFilters.push({
      ...filter,
      fields: filter.props?.labels?.map(({ label }) => ({
        label,
        value: JSON.stringify(filteredValues),
      })),
    });
  }
};

export const filterFilteropties = (filter: Filter, newFilters: Filter[]) => {
  if (filter.id === 'filteropties') {
    newFilters.push({
      ...filter,
      fields: filter.props?.labels?.map(({ label, value, optionTooltip }) => ({
        label,
        value,
        optionTooltip,
      })),
    });
  }
};

export const filterWithoutKey = (filter: Filter, newFilters: Filter[]) => {
  if (filter && (!filter.key || filter.preserveFields)) {
    const fields = filter.fields ?? [];
    newFilters.push({
      ...filter,
      fields: [...fields],
    });
  }
};

export const filterWithKey = (
  filter: Filter,
  fields: FilterItem[],
  newFilters: Filter[]
) => {
  if (
    filter.key &&
    !filter.props?.minValue &&
    !filter.preserveFields &&
    !newFilters.some((f) => f.id === filter.id)
  ) {
    newFilters.push({
      ...filter,
      fields: sortBy(fields, 'label'),
    });
  }
};

export const loopProductsToGenerateFields = (
  products: IProduct[],
  filter: Filter,
  fields: FilterItem[]
) => {
  for (const product of products) {
    const value = get(product, filter.key);

    if (
      !fields.find((field) => field.value == value) &&
      typeof value === 'string'
    ) {
      fields.push({
        label: value,
        value,
      });
    }
  }
  // Temporary workaround: the following if statement will be removed
  // when oneOffCostsAmount are added in the product selling points
  if (
    filter.key === 'oneOffCostsAmount' ||
    filter.key === 'purchasableProduct' ||
    filter.key === 'garantiestelselNederland'
  ) {
    fields.push({
      label: filter.fields[0].label,
      value: filter.fields[0].value,
      optionTooltip: filter.fields[0].optionTooltip,
    });
  }
};

export abstract class AbstractComparatorApiService
  implements IComparatorApiService
{
  baseUrl = '';
  baseUrlTSN = '';
  credentials: IComparatorApiService['credentials'] = {
    user: '',
    password: '',
    token: '',
    clientId: '',
    clientSecret: '',
    expiresIn: undefined,
  };

  constructor(initializationParams: IComparatorApiServiceParameters) {
    for (const attr in initializationParams) {
      this[attr] = initializationParams[attr];
    }
  }
  abstract getClient(): any;
  abstract getComparison(id: string): Promise<Comparison>;
  abstract getCustomerBenefits(): Promise<CustormerBenefitItem[]>;
  abstract validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse>;
  abstract getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData>;
  abstract getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection>;
  abstract calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure>;
  abstract getProducts(
    searchCriteria: object,
    selectedFilters?: object,
    requestOptions?: Parameters<typeof fetch>[1],
    quotePrefillProps?: { swiftUserId: string; swiftProductId: string },
    useOldValidation?: boolean
  ): Promise<IProduct[]>;
  abstract orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string,
    affiliateIncentive?: {
      userDesiresIncentive: boolean;
      callName?: string;
      prefixes?: string;
      surname?: string;
      birthDate?: string;
      email?: string;
    },
    comparatorName?: string
  ): Promise<OrderResult>;
  abstract sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity?: boolean,
    intake?: boolean,
    continueCoverage?: boolean
  ): Promise<string>;

  abstract getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure>;

  abstract orderStatus(orderId: string): Promise<OrderStatusResult>;

  abstract getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderResponse['data']>;
  abstract getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> | undefined;

  sortProducts(
    products: IProduct[],
    reference: string,
    descending = false
  ): IProduct[] {
    let sortedProducts: IProduct[];

    if (reference === 'testScore') {
      // Sort by test score, then by price
      sortedProducts = products.sort((a, b) => {
        const aScore = a.testScore ?? -Infinity;
        const bScore = b.testScore ?? -Infinity;

        if (aScore !== bScore) {
          return descending ? bScore - aScore : aScore - bScore;
        }
        // Sort by price when test scores are equal or both undefined
        return a.priceInfo.total - b.priceInfo.total;
      });
    } else {
      // Sort by the specified reference
      sortedProducts = products.sort((a, b) => {
        const aValue = get(a, reference);
        const bValue = get(b, reference);

        if (aValue === undefined && bValue === undefined) return 0;
        if (aValue === undefined) return descending ? -1 : 1;
        if (bValue === undefined) return descending ? 1 : -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return descending
            ? bValue.localeCompare(aValue)
            : aValue.localeCompare(bValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return descending ? bValue - aValue : aValue - bValue;
        }

        // If types don't match or are not sortable, maintain original order
        return 0;
      });
    }

    return sortedProducts;
  }

  getFilters(products: IProduct[], filters: Filter[]): Filter[] {
    let newFilters: Filter[] = [];

    if (!filters?.length) {
      return newFilters;
    }

    for (const filter of filters) {
      if (filter.id === 'showPriceMode') {
        newFilters.push(filter);
      } else {
        let fields: FilterItem[] = [];

        loopProductsToGenerateFields(products, filter, fields);

        filterSustainabilityScore(filter, fields, newFilters);
        filterFilteropties(filter, newFilters);
        filterWithoutKey(filter, newFilters);
        filterWithKey(filter, fields, newFilters);
      }
    }
    return newFilters;
  }

  addEnergyAuction(variables: any) {
    return Promise.resolve(variables);
  }

  abstract checkMembershipStatus(email: string): Promise<any>;
}

export interface IGraphProvider {
  query<T>(
    query: any,
    variables: object,
    dataName: string,
    fetchPolicy?: FetchPolicy,
    newClientLink?: string
  ): Promise<T>;
  mutate<T>(
    query: any,
    variables: object,
    dataName: string,
    fetchPolicy?: MutationFetchPolicy,
    newClientLink?: string
  ): Promise<T>;
}
